import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuth } from './auth/AuthContext';
import LoadingScreen from './components/LoadingScreen';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ResetPasswordPage from './pages/ResetPasswordPage';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TermsAcceptancePage = lazy(() => import('./pages/TermsAcceptancePage'));
const ShopifyOnboardingPage = lazy(() => import('./pages/ShopifyOnboardingPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
// Hidden admin-only style gallery — in no nav, reachable by direct URL only.
// Its own admin gate + redirect live in the page component.
const LookbookPage = lazy(() => import('./pages/LookbookPage'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

/**
 * Triggers Keycloak login when rendered.
 * Used as catch-all for unauthenticated users on non-public routes
 * (e.g. SPA navigation from /accept-invite after completing setup) and
 * as the handler for the bookmarkable `/login` entry point.
 *
 * Preserves the current path + search so post-login the merchant lands
 * back on the page they were trying to reach (e.g. the Shopify
 * onboarding redirect from the OAuth callback).
 */
const TriggerLogin = () => {
  const { loginWithPassword } = useAuth();
  useEffect(() => {
    const target = window.location.pathname + window.location.search;
    void loginWithPassword(target || '/');
  }, []);
  return null;
};

const App = () => {
  const { loading, authenticated, user } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  // Custom-terms hard gate: a designated signer who owes a signature cannot
  // reach the dashboard until they accept. Set only for that signer, so other
  // users (including non-signer admins) are unaffected.
  if (authenticated && user?.termsAcceptanceRequired) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <TermsAcceptancePage />
      </Suspense>
    );
  }

  if (!authenticated) {
    return (
      <BrowserRouter>
        <SentryRoutes>
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/login" element={<TriggerLogin />} />
          <Route path="*" element={<TriggerLogin />} />
        </SentryRoutes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <SentryRoutes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/onboarding/shopify" element={<ShopifyOnboardingPage />} />
          <Route path="/lookbook" element={<LookbookPage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </SentryRoutes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
